import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Nav,
    NavItem,
    NavLink,
    Row,
    Spinner,
    Table,
} from 'reactstrap';
import {
    BrowserRouter as Router,
    Link,
    Redirect,
    Route,
    Switch,
    useHistory,
    useLocation,
    useParams,
} from 'react-router-dom';
import {
    buildFinanceExceptionBreakdown,
    getFinancePaymentExceptionCount,
    getRecentNetCapturedTotal,
} from './paymentReconciliationUtils';
import { getBackofficeRoot } from '../store/requests';
import MenuCatalogManager from '../ui/menu/MenuCatalogManager';

const PARTNER_TOKEN_KEY = 'partner_token';

const BILL_STATUS_LABELS = {
    1: 'Open',
    2: 'Closed',
    3: 'Canceled',
    4: 'Completed',
    5: 'Manual',
    6: 'Completed (Manual)',
};

const TRANSACTION_STATUS_LABELS = {
    1: 'Processing',
    2: 'Reprocessing',
    3: 'Approved',
    4: 'Failed',
    5: 'Declined',
    6: 'Canceled',
};

function getPartnerToken() {
    return window.localStorage.getItem(PARTNER_TOKEN_KEY);
}

function setPartnerToken(token) {
    window.localStorage.setItem(PARTNER_TOKEN_KEY, token);
}

function unsetPartnerToken() {
    window.localStorage.removeItem(PARTNER_TOKEN_KEY);
}

function getPartnerApiRoot() {
    const explicitRoot = process.env.REACT_APP_PARTNER_ROOT;
    if (explicitRoot) {
        return explicitRoot.replace(/\/partners\/?$/, '').replace(/\/+$/, '');
    }

    const backofficeRoot = getBackofficeRoot();
    if (backofficeRoot) {
        return backofficeRoot.replace(/\/backoffice\/?$/, '').replace(/\/+$/, '');
    }

    return '/api';
}

function getPublicApiOrigin() {
    const root = getPartnerApiRoot();
    if (/^https?:\/\//i.test(root)) {
        return root.replace(/\/+$/, '');
    }
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
        // Admin host proxies backend under /api
        return `${window.location.origin}/api`;
    }
    return root.replace(/\/+$/, '') || '/api';
}

function resolveLocationLogoUrl(location) {
    if (!location || !location.id || !location.logo) {
        return null;
    }
    return `${getPublicApiOrigin()}/site/organization-locations/${encodeURIComponent(location.id)}/image-logo`;
}

function resolveLocationCoverUrl(location) {
    if (!location || !location.id) {
        return null;
    }
    if (location.cover) {
        return `${getPublicApiOrigin()}/site/organization-locations/${encodeURIComponent(location.id)}/image-cover`;
    }
    return null;
}

function pickPrimaryLocation(locations) {
    const list = Array.isArray(locations) ? locations : [];
    if (!list.length) {
        return null;
    }
    return list.find((location) => location && location.logo) || list[0];
}

function buildPartnerUrl(path) {
    const cleanPath = path.replace(/^\/+/, '');
    return `${getPartnerApiRoot()}/partners/${cleanPath}`;
}

function buildPaginationQuery(page, perPage) {
    return `pagination=${encodeURIComponent(JSON.stringify({ page, perPage }))}`;
}

function encodeBasicAuth(email, password) {
    return `Basic ${btoa(`${email}:${password}`)}`;
}

async function parseJsonResponse(response) {
    if (response.ok) {
        return response.json();
    }

    let message = `Request failed with status ${response.status}`;
    try {
        const payload = await response.json();
        if (payload && payload.message) {
            message = payload.message;
        }
    } catch (error) {
        // Keep the generic message if the payload is not JSON.
    }

    const requestError = new Error(message);
    requestError.status = response.status;
    throw requestError;
}

async function partnerLogin(email, password) {
    const response = await fetch(buildPartnerUrl('/organizations/my/users/token'), {
        method: 'GET',
        headers: {
            Authorization: encodeBasicAuth(email, password),
        },
    });

    return parseJsonResponse(response);
}

async function partnerLogout(token) {
    const response = await fetch(buildPartnerUrl('/organizations/my/users/token'), {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    return parseJsonResponse(response);
}

async function partnerGet(path, token) {
    const response = await fetch(buildPartnerUrl(path), {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    return parseJsonResponse(response);
}

async function partnerPost(path, token, body) {
    const response = await fetch(buildPartnerUrl(path), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });

    return parseJsonResponse(response);
}

async function partnerPut(path, token, body) {
    const response = await fetch(buildPartnerUrl(path), {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });

    return parseJsonResponse(response);
}

function toMoney(amount) {
    return Number(amount || 0) / 100;
}

function formatCurrency(amount) {
    const safeAmount = toMoney(amount);
    return safeAmount.toLocaleString('en-CA', {
        style: 'currency',
        currency: 'CAD',
    });
}

function formatDateTime(value) {
    if (!value) {
        return 'Unavailable';
    }
    const numericValue = Number(value);
    const timestamp = Number.isFinite(numericValue) && numericValue < 1e12 ? numericValue * 1000 : numericValue;
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleString('en-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function formatPaymentMode(value) {
    switch (Number(value)) {
        case 1:
            return 'Test';
        case 2:
            return 'Mock';
        case 3:
            return 'Real';
        default:
            return 'Unavailable';
    }
}

function formatBps(value) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
        return 'Not configured';
    }
    return `${numericValue} bps (${(numericValue / 100).toFixed(2)}%)`;
}

function formatRefundReason(transaction) {
    const code = normalizeText(transaction && transaction.refund_reason_code, '');
    const note = normalizeText(transaction && transaction.refund_reason, '');
    if (!code && !note) {
        return '—';
    }
    if (code && note && note.toLowerCase() !== code.toLowerCase()) {
        return `${code}: ${note}`;
    }
    return note || code;
}

function formatTransactionType(value) {
    switch (Number(value)) {
        case 1:
            return 'Pre-auth';
        case 2:
            return 'Capture';
        case 3:
            return 'Refund';
        default:
            return 'Unknown';
    }
}

function formatRewardTransactionType(value) {
    switch (Number(value)) {
        case 1:
            return 'Award';
        case 2:
            return 'Redemption';
        case 3:
            return 'Adjustment';
        default:
            return 'Unknown';
    }
}

function formatSupportStatus(value) {
    const normalized = normalizeText(value, 'new');
    return normalized
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function normalizeText(value, fallback = 'Unavailable') {
    if (!value) {
        return fallback;
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number') {
        return String(value);
    }

    if (Array.isArray(value)) {
        return value.map((item) => normalizeText(item, '')).filter(Boolean).join(', ') || fallback;
    }

    if (typeof value === 'object') {
        if (typeof value.text === 'string' && value.text.trim()) {
            return value.text;
        }
        if (value.display_name) {
            return value.display_name;
        }
        if (value.name) {
            return normalizeText(value.name, fallback);
        }
        if (value.en || value.fr) {
            return value.en || value.fr;
        }
        if (value.id) {
            return value.id;
        }
    }

    return fallback;
}

function summarizeBills(bills) {
    const sortedBills = [...bills].sort((left, right) => {
        const leftTime = new Date(left.created_at || 0).getTime();
        const rightTime = new Date(right.created_at || 0).getTime();
        return rightTime - leftTime;
    });

    const completedStatuses = new Set([4, 6]);
    const openStatuses = new Set([1, 2]);

    const totals = sortedBills.reduce((accumulator, bill) => {
        const status = Number(bill.status);
        accumulator.gross += Number(bill.total || 0);
        accumulator.rewards += Number(bill.rewards_discount || 0);

        if (completedStatuses.has(status)) {
            accumulator.completedCount += 1;
        }

        if (openStatuses.has(status)) {
            accumulator.openCount += 1;
        }

        return accumulator;
    }, {
        gross: 0,
        rewards: 0,
        completedCount: 0,
        openCount: 0,
    });

    return {
        ...totals,
        recentBills: sortedBills.slice(0, 8),
        transactions: sortedBills.slice(0, 50),
        lastActivityAt: sortedBills[0] ? sortedBills[0].created_at : null,
        totalBills: sortedBills.length,
    };
}

function getSupportStatusColor(status) {
    switch (status) {
        case 'new':
            return 'secondary';
        case 'acknowledged':
            return 'info';
        case 'assigned':
            return 'primary';
        case 'contacted':
            return 'warning';
        case 'nurturing':
            return 'dark';
        case 'won':
            return 'success';
        case 'lost':
            return 'danger';
        case 'closed':
            return 'secondary';
        default:
            return 'light';
    }
}

function getSettlementHealthColor(status) {
    switch (status) {
        case 'healthy':
            return 'success';
        case 'watch':
            return 'warning';
        case 'risk':
            return 'danger';
        default:
            return 'secondary';
    }
}

function formatSettlementHealth(status) {
    switch (status) {
        case 'healthy':
            return 'Healthy';
        case 'watch':
            return 'Watch';
        case 'risk':
            return 'Risk';
        default:
            return 'Unknown';
    }
}

function HeroHeader({ organization, locations, profile, onLogout, isLoggingOut }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const roleLabel = profile && Number(profile.role) === 2 ? 'Owner' : 'Manager';
    const primaryLocation = pickPrimaryLocation(locations);
    const restaurantName = normalizeText(
        (primaryLocation && primaryLocation.name) || (organization && organization.name),
        'Your restaurant',
    );
    const logoUrl = resolveLocationLogoUrl(primaryLocation);
    const coverUrl = resolveLocationCoverUrl(primaryLocation);
    const locationCount = Array.isArray(locations) ? locations.length : 0;
    const heroStyle = coverUrl
        ? {
            backgroundImage:
                `linear-gradient(120deg, rgba(15, 23, 42, 0.88) 0%, rgba(15, 23, 42, 0.72) 48%, rgba(22, 78, 99, 0.55) 100%), url(${coverUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
        }
        : undefined;

    return (
        <div data-testid="partner-hero-header" className="partner-portal-hero" style={heroStyle}>
            <Container className="partner-portal-hero-inner">
                <div className="partner-portal-hero-bar">
                    <div className="partner-portal-brand">
                        <div className="partner-portal-logo-wrap" aria-hidden={!logoUrl}>
                            {logoUrl ? (
                                <img
                                    src={logoUrl}
                                    alt=""
                                    className="partner-portal-logo"
                                    data-testid="partner-restaurant-logo"
                                />
                            ) : (
                                <div className="partner-portal-logo-fallback">
                                    {restaurantName.slice(0, 1).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className="partner-portal-brand-copy">
                            <div className="partner-portal-hero-kicker">Owner dashboard</div>
                            <h1 className="partner-portal-hero-title" data-testid="partner-restaurant-name">
                                {restaurantName}
                            </h1>
                            <p className="partner-portal-hero-copy">
                                Bills, payments, loyalty, and guest support for your floor.
                                {locationCount > 1 ? ` ${locationCount} locations in scope.` : ''}
                            </p>
                        </div>
                    </div>
                    <div className="partner-portal-hero-meta">
                        <Dropdown
                            isOpen={menuOpen}
                            toggle={() => setMenuOpen((open) => !open)}
                            data-testid="partner-account-menu"
                        >
                            <DropdownToggle
                                caret
                                color="light"
                                className="partner-account-menu-toggle"
                                disabled={isLoggingOut}
                            >
                                {roleLabel}
                            </DropdownToggle>
                            <DropdownMenu right className="partner-account-menu">
                                <DropdownItem header>
                                    {profile ? normalizeText(profile.email) : 'Account'}
                                </DropdownItem>
                                <DropdownItem divider />
                                <DropdownItem
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setMenuOpen(false);
                                        if (onLogout) {
                                            onLogout();
                                        }
                                    }}
                                    disabled={isLoggingOut}
                                    data-testid="partner-logout-item"
                                    className="partner-logout-item"
                                >
                                    {isLoggingOut ? 'Signing out...' : 'Log out'}
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                </div>
            </Container>
        </div>
    );
}

function PortalNav() {
    const location = useLocation();

    const items = [
        { to: '/overview', label: 'Overview' },
        { to: '/activity', label: 'Activity' },
        { to: '/transactions', label: 'Transactions' },
        { to: '/fees-and-settlements', label: 'Fees & Settlements' },
        { to: '/loyalty', label: 'Loyalty' },
        { to: '/menu', label: 'Menu' },
        { to: '/support', label: 'Support' },
    ];

    return (
        <div data-testid="partner-portal-nav" className="partner-portal-nav">
            <Container>
                <Nav className="partner-portal-nav-scroller" pills>
                    {items.map((item) => {
                        const isActive = location.pathname === item.to
                            || (item.to !== '/overview' && location.pathname.startsWith(item.to));
                        return (
                            <NavItem key={item.to}>
                                <NavLink
                                    tag={Link}
                                    to={item.to}
                                    active={isActive}
                                    className={`partner-portal-nav-link${isActive ? ' is-active' : ''}`}
                                >
                                    {item.label}
                                </NavLink>
                            </NavItem>
                        );
                    })}
                </Nav>
            </Container>
        </div>
    );
}

function getTransactionStatusCode(transaction) {
    return Number(transaction && (transaction.tx_status != null ? transaction.tx_status : transaction.status));
}

function getTransactionTypeCode(transaction) {
    return Number(transaction && transaction.tx_type);
}

function isExceptionTransaction(transaction) {
    const status = getTransactionStatusCode(transaction);
    return status === 4 || status === 5 || status === 6;
}

function isProcessingTransaction(transaction) {
    const status = getTransactionStatusCode(transaction);
    return status === 1 || status === 2;
}

function isApprovedTransaction(transaction) {
    return getTransactionStatusCode(transaction) === 3;
}

function isRefundTransaction(transaction) {
    return getTransactionTypeCode(transaction) === 3;
}

function isCaptureTransaction(transaction) {
    return getTransactionTypeCode(transaction) === 2;
}

function isOpenBill(bill) {
    const status = Number(bill && bill.status);
    return status === 1 || status === 2;
}

function isCompletedBill(bill) {
    const status = Number(bill && bill.status);
    return status === 4 || status === 6;
}

function filterTransactions(transactions, filterKey) {
    const list = Array.isArray(transactions) ? transactions : [];
    switch (filterKey) {
        case 'exceptions':
            return list.filter(isExceptionTransaction);
        case 'refunds':
            return list.filter(isRefundTransaction);
        case 'processing':
            return list.filter(isProcessingTransaction);
        case 'approved':
            return list.filter(isApprovedTransaction);
        case 'captures':
            return list.filter(isCaptureTransaction);
        default:
            return list;
    }
}

function filterBills(bills, filterKey) {
    const list = Array.isArray(bills) ? bills : [];
    switch (filterKey) {
        case 'open':
            return list.filter(isOpenBill);
        case 'completed':
            return list.filter(isCompletedBill);
        default:
            return list;
    }
}

function SummaryCard({
    label,
    value,
    hint,
    onClick,
    active = false,
    tone = 'neutral',
    testId,
    ctaLabel,
}) {
    const interactive = typeof onClick === 'function';
    const className = [
        'partner-metric-card',
        `tone-${tone}`,
        interactive ? 'is-action' : 'is-static',
        active ? 'is-active' : '',
    ].filter(Boolean).join(' ');

    return (
        <Card
            className={className}
            data-testid={testId}
            onClick={interactive ? onClick : undefined}
            onKeyDown={interactive ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick();
                }
            } : undefined}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-pressed={interactive ? active : undefined}
        >
            <CardBody>
                <div className="partner-metric-top">
                    <div className="partner-metric-label">{label}</div>
                    {interactive ? (
                        <span className={`partner-metric-affordance${active ? ' is-active' : ''}`}>
                            {active ? 'Open' : 'Tap'}
                        </span>
                    ) : (
                        <span className="partner-metric-affordance is-static">Info</span>
                    )}
                </div>
                <div className="partner-metric-value">{value}</div>
                <div className="partner-metric-help">{hint}</div>
                {interactive ? (
                    <div className="partner-metric-cta">
                        {ctaLabel || (active ? 'Showing details' : 'View details')}
                        <span className="partner-metric-cta-arrow" aria-hidden="true">&gt;</span>
                    </div>
                ) : (
                    <div className="partner-metric-static-note">Summary only</div>
                )}
            </CardBody>
        </Card>
    );
}

function DetailPageChrome({
    title,
    subtitle,
    backTo,
    backLabel = 'Back',
    testId,
    children,
    trailing,
}) {
    const history = useHistory();
    return (
        <Container className="py-4" data-testid={testId}>
            <div className="partner-page-intro mb-3">
                <Button
                    color="link"
                    className="pl-0 mb-2 text-decoration-none"
                    onClick={() => history.push(backTo)}
                    data-testid="partner-detail-back"
                >
                    &larr; {backLabel}
                </Button>
                <div className="d-flex flex-wrap justify-content-between align-items-start" style={{ gap: 12 }}>
                    <div>
                        <h2 className="mb-1">{title}</h2>
                        {subtitle ? <p className="mb-0 text-muted">{subtitle}</p> : null}
                    </div>
                    {trailing || null}
                </div>
            </div>
            {children}
        </Container>
    );
}

function DetailFields({ fields }) {
    return (
        <div className="partner-detail-grid" data-testid="partner-detail-fields">
            {fields.map((field) => (
                <div className="partner-detail-item" key={field.label}>
                    <div className="partner-detail-item-label">{field.label}</div>
                    <div className="partner-detail-item-value">{field.value}</div>
                </div>
            ))}
        </div>
    );
}

function DetailModal({
    isOpen,
    title,
    subtitle,
    onClose,
    testId,
    children,
}) {
    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            centered
            size="lg"
            className="partner-detail-modal"
            data-testid={testId}
        >
            <ModalHeader toggle={onClose} className="partner-detail-modal-header">
                <div>
                    <div className="partner-panel-title">{title}</div>
                    {subtitle ? <div className="partner-panel-subtitle">{subtitle}</div> : null}
                </div>
            </ModalHeader>
            <ModalBody>
                {children}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onClose} data-testid={`${testId || 'partner-detail'}-close`}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
}

function BillDetailPanel({ bill, onClose }) {
    return (
        <DetailModal
            isOpen={Boolean(bill)}
            title="Bill details"
            subtitle="Guest check snapshot from your organization scope."
            onClose={onClose}
            testId="partner-bill-detail"
        >
            {bill ? (
                <DetailFields
                    fields={[
                        { label: 'Bill id', value: normalizeText(bill.id) },
                        { label: 'Status', value: BILL_STATUS_LABELS[bill.status] || normalizeText(bill.status) },
                        { label: 'Total', value: formatCurrency(bill.total) },
                        { label: 'Rewards discount', value: formatCurrency(bill.rewards_discount) },
                        { label: 'Created', value: formatDateTime(bill.created_at) },
                        { label: 'Updated', value: formatDateTime(bill.updated_at || bill.created_at) },
                        { label: 'Location', value: normalizeText(bill.location_id || bill.location_name, 'Organization scope') },
                        { label: 'Table / seat', value: normalizeText([bill.pos_table_nr, bill.pos_customer_nr].filter(Boolean).join(' / '), 'Not assigned') },
                    ]}
                />
            ) : null}
        </DetailModal>
    );
}

function TransactionDetailPanel({ transaction, onClose }) {
    const providerRef = transaction ? normalizeText(
        transaction.provider_tx_id
        || transaction.payfacto_tx_no
        || transaction.payfacto_auth_no
        || transaction.payfacto_merchant_no,
        'Pending',
    ) : '';

    return (
        <DetailModal
            isOpen={Boolean(transaction)}
            title="Payment details"
            subtitle="Provider and settlement fields visible to your restaurant."
            onClose={onClose}
            testId="partner-transaction-detail"
        >
            {transaction ? (
                <DetailFields
                    fields={[
                        { label: 'Transaction id', value: normalizeText(transaction.id) },
                        { label: 'Bill', value: normalizeText(transaction.bill_id) },
                        { label: 'Type', value: formatTransactionType(transaction.tx_type) },
                        {
                            label: 'Status',
                            value: normalizeText(
                                TRANSACTION_STATUS_LABELS[getTransactionStatusCode(transaction)],
                                'Unknown',
                            ),
                        },
                        { label: 'Amount', value: formatCurrency(transaction.amount) },
                        { label: 'Provider', value: normalizeText(transaction.provider || 'payfacto') },
                        { label: 'Provider reference', value: providerRef },
                        { label: 'Created', value: formatDateTime(transaction.created_at) },
                        {
                            label: 'Refund reason',
                            value: isRefundTransaction(transaction) ? formatRefundReason(transaction) : 'Not a refund',
                        },
                        {
                            label: 'Merchant',
                            value: normalizeText(transaction.payfacto_merchant_no || transaction.merchant_no, 'Unavailable'),
                        },
                    ]}
                />
            ) : null}
        </DetailModal>
    );
}

function OperationalSignalCard({ label, value, hint, onClick, ctaLabel, testId }) {
    return (
        <SummaryCard
            label={label}
            value={value}
            hint={hint}
            tone="healthy"
            onClick={onClick}
            ctaLabel={ctaLabel}
            testId={testId}
        />
    );
}

function ResourceAlert({ error }) {
    if (!error) {
        return null;
    }

    return (
        <Alert color="warning" className="mb-3">
            {error}
        </Alert>
    );
}

function OverviewPage({ organization, locations, summary }) {
    const history = useHistory();
    const [selectedBill, setSelectedBill] = useState(null);

    const recentBills = summary.recentBills || [];

    return (
        <Container className="py-4" data-testid="partner-overview-page">
            <div className="partner-page-intro">
                <h2>Overview</h2>
                <p>Tap open or completed bills to jump straight into Activity with that filter applied.</p>
            </div>
            <Row>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Gross billed"
                        value={formatCurrency(summary.gross)}
                        hint="Organization-scoped bill totals currently visible in this portal."
                        tone="neutral"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Completed bills"
                        value={summary.completedCount}
                        hint="Cleared guest checks - opens Activity filtered to completed."
                        tone="healthy"
                        ctaLabel="Open activity"
                        onClick={() => history.push('/activity/completed')}
                        testId="partner-overview-card-completed"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Open or closed bills"
                        value={summary.openCount}
                        hint="Live or unsettled checks - opens Activity filtered to open/closed."
                        tone="warning"
                        ctaLabel="Open activity"
                        onClick={() => history.push('/activity/open')}
                        testId="partner-overview-card-open"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Rewards discounts"
                        value={formatCurrency(summary.rewards)}
                        hint="Loyalty discounts already applied on visible bills."
                        tone="neutral"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Active locations"
                        value={locations.length}
                        hint="Locations currently in your restaurant scope."
                        tone="neutral"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Last activity"
                        value={formatDateTime(summary.lastActivityAt)}
                        hint="Most recent bill timestamp in the current partner feed."
                        tone="neutral"
                    />
                </Col>
            </Row>

            <BillDetailPanel bill={selectedBill} onClose={() => setSelectedBill(null)} />

            <Row className="mt-2">
                <Col lg="7" className="mb-3">
                    <Card className="partner-panel" data-testid="partner-overview-transactions-card">
                        <CardBody>
                            <div className="partner-panel-header">
                                <div>
                                    <h2 className="partner-panel-title">Recent bills</h2>
                                    <p className="partner-panel-subtitle">Tap a row for check details, or open Activity for the live floor list.</p>
                                </div>
                                <Button color="secondary" outline size="sm" onClick={() => history.push('/activity')}>
                                    Open activity
                                </Button>
                            </div>
                            <Table responsive hover className="mb-0" data-testid="partner-overview-transactions-table">
                                <thead>
                                    <tr>
                                        <th>Created</th>
                                        <th>Status</th>
                                        <th>Total</th>
                                        <th>Rewards</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentBills.map((bill) => (
                                        <tr
                                            key={bill.id}
                                            className={`partner-row-action${selectedBill && selectedBill.id === bill.id ? ' is-selected' : ''}`}
                                            onClick={() => setSelectedBill(bill)}
                                        >
                                            <td>{formatDateTime(bill.created_at)}</td>
                                            <td>{BILL_STATUS_LABELS[bill.status] || bill.status}</td>
                                            <td>{formatCurrency(bill.total)}</td>
                                            <td>{formatCurrency(bill.rewards_discount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="5" className="mb-3">
                    <Card className="partner-panel" data-testid="partner-overview-notes-card">
                        <CardBody>
                            <h2 className="partner-panel-title mb-3">How to use this portal</h2>
                            <Alert color="secondary">
                                This is your restaurant workspace - not the BillMaster admin backoffice. Navigation stays under /partners.
                            </Alert>
                            <ul className="mb-0" style={{ paddingLeft: '1.2rem' }}>
                                <li>Use Activity for live bills and guest sessions at your locations.</li>
                                <li>Use Transactions and Fees for payment transparency and reconciliation.</li>
                                <li>Handle guest issues in Support first; escalate only special cases to BillMaster.</li>
                            </ul>
                            <hr />
                            <div>
                                <strong>Organization:</strong> {normalizeText(organization && organization.name)}
                            </div>
                            <div>
                                <strong>Locations in scope:</strong> {locations.length}
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

const ACTIVITY_VIEWS = {
    bills: {
        filter: null,
        title: 'All bills',
        subtitle: 'Organization-scoped guest checks for table-side and end-of-shift review.',
    },
    open: {
        filter: 'open',
        title: 'Open / closed bills',
        subtitle: 'Live or unsettled bills that may need floor staff attention.',
    },
    completed: {
        filter: 'completed',
        title: 'Completed bills',
        subtitle: 'Cleared guest checks ready for review and reconciliation.',
    },
    payments: {
        filter: null,
        title: 'Latest payments',
        subtitle: 'Recent payment events from the floor. Open Transactions for the full feed.',
        paymentsOnly: true,
    },
};

function ActivityHubPage({ bills, locations, transactions }) {
    const history = useHistory();
    const recentBills = useMemo(() => (
        [...(bills || [])]
            .sort((left, right) => Number(right.created_at || 0) - Number(left.created_at || 0))
    ), [bills]);
    const openBills = useMemo(() => recentBills.filter(isOpenBill), [recentBills]);
    const completedBills = useMemo(() => recentBills.filter(isCompletedBill), [recentBills]);
    const paymentCount = (transactions || []).length;

    return (
        <Container className="py-4" data-testid="partner-activity-page">
            <div className="partner-page-intro">
                <h2>Activity</h2>
                <p>Tap a tile to open its detail page. Each view stays focused so the floor list is not buried under the hub.</p>
            </div>
            <Row>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Bills in view"
                        value={recentBills.length}
                        hint="Organization-scoped bill activity available to owners and managers."
                        tone="neutral"
                        ctaLabel="Open list"
                        onClick={() => history.push('/activity/bills')}
                        testId="partner-activity-card-all"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Open / closed"
                        value={openBills.length}
                        hint="Live or unsettled bills that may need floor staff attention."
                        tone="warning"
                        ctaLabel="Open list"
                        onClick={() => history.push('/activity/open')}
                        testId="partner-activity-card-open"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Completed"
                        value={completedBills.length}
                        hint="Cleared guest checks ready for review and reconciliation."
                        tone="healthy"
                        ctaLabel="Open list"
                        onClick={() => history.push('/activity/completed')}
                        testId="partner-activity-card-completed"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Latest payments"
                        value={paymentCount}
                        hint="Jump to recent payment events, or open the full Transactions feed."
                        tone="neutral"
                        ctaLabel="Open list"
                        onClick={() => history.push('/activity/payments')}
                        testId="partner-activity-card-payments"
                    />
                </Col>
            </Row>
            <Row>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Locations"
                        value={locations.length}
                        hint="Locations currently visible in your partner scope."
                        tone="neutral"
                    />
                </Col>
            </Row>
        </Container>
    );
}

function ActivityDetailPage({ bills, transactions }) {
    const history = useHistory();
    const { view } = useParams();
    const config = ACTIVITY_VIEWS[view];
    const [selectedBill, setSelectedBill] = useState(null);
    const [selectedTransaction, setSelectedTransaction] = useState(null);

    const recentBills = useMemo(() => (
        [...(bills || [])]
            .sort((left, right) => Number(right.created_at || 0) - Number(left.created_at || 0))
    ), [bills]);
    const visibleBills = useMemo(
        () => filterBills(recentBills, config ? config.filter : null),
        [recentBills, config],
    );
    const recentPayments = useMemo(() => (
        [...(transactions || [])]
            .sort((left, right) => Number(right.created_at || 0) - Number(left.created_at || 0))
            .slice(0, 40)
    ), [transactions]);

    if (!config) {
        return <Redirect to="/activity" />;
    }

    if (config.paymentsOnly) {
        return (
            <DetailPageChrome
                title={config.title}
                subtitle={config.subtitle}
                backTo="/activity"
                backLabel="Back to Activity"
                testId="partner-activity-payments-page"
                trailing={(
                    <Button color="secondary" outline size="sm" onClick={() => history.push('/transactions')}>
                        All payments
                    </Button>
                )}
            >
                <TransactionDetailPanel transaction={selectedTransaction} onClose={() => setSelectedTransaction(null)} />
                <Card className="partner-panel" data-testid="partner-activity-payments-card">
                    <CardBody>
                        <Table responsive hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th>Provider</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentPayments.length ? recentPayments.map((transaction) => (
                                    <tr
                                        key={transaction.id}
                                        className={`partner-row-action${selectedTransaction && selectedTransaction.id === transaction.id ? ' is-selected' : ''}`}
                                        onClick={() => setSelectedTransaction(transaction)}
                                    >
                                        <td>{formatDateTime(transaction.created_at)}</td>
                                        <td>{normalizeText(TRANSACTION_STATUS_LABELS[getTransactionStatusCode(transaction)], 'Unknown')}</td>
                                        <td>{formatCurrency(transaction.amount)}</td>
                                        <td>{normalizeText(transaction.provider, '-')}</td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="4" className="text-muted">No recent payment events.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>
            </DetailPageChrome>
        );
    }

    return (
        <DetailPageChrome
            title={config.title}
            subtitle={config.subtitle}
            backTo="/activity"
            backLabel="Back to Activity"
            testId="partner-activity-detail-page"
            trailing={<Badge color="secondary" pill className="px-3 py-2">{visibleBills.length} bills</Badge>}
        >
            <BillDetailPanel bill={selectedBill} onClose={() => setSelectedBill(null)} />
            <Card className="partner-panel" data-testid="partner-activity-bills-card">
                <CardBody>
                    <Table responsive hover className="mb-0" data-testid="partner-activity-bills-table">
                        <thead>
                            <tr>
                                <th>Created</th>
                                <th>Bill</th>
                                <th>Status</th>
                                <th>Total</th>
                                <th>Rewards</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleBills.length ? visibleBills.map((bill) => (
                                <tr
                                    key={bill.id}
                                    className={`partner-row-action${selectedBill && selectedBill.id === bill.id ? ' is-selected' : ''}`}
                                    onClick={() => setSelectedBill(bill)}
                                >
                                    <td>{formatDateTime(bill.created_at)}</td>
                                    <td><code>{bill.id}</code></td>
                                    <td>{BILL_STATUS_LABELS[bill.status] || bill.status}</td>
                                    <td>{formatCurrency(bill.total)}</td>
                                    <td>{formatCurrency(bill.rewards_discount)}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="text-muted">No bill activity is visible for this organization yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>
        </DetailPageChrome>
    );
}

const TRANSACTION_VIEWS = {
    all: { filter: null, title: 'All transactions', subtitle: 'Live payment events for your organization. Tap a row for full detail.' },
    exceptions: { filter: 'exceptions', title: 'Exceptions', subtitle: 'Failed, declined, or canceled events that need direct explanation.' },
    refunds: { filter: 'refunds', title: 'Refunds', subtitle: 'Refund volume already visible across your merchant footprint.' },
    processing: { filter: 'processing', title: 'Processing queue', subtitle: 'Events still processing or reprocessing that may raise staff questions.' },
    approved: { filter: 'approved', title: 'Approved', subtitle: 'Approved captures, pre-auths, and refunds in the current feed.' },
    captures: { filter: 'captures', title: 'Captures', subtitle: 'Capture events visible in the current organization feed.' },
};

function TransactionsHubPage({ transactions, transactionSummary, resourceErrors = {} }) {
    const history = useHistory();
    const captureCount = useMemo(
        () => filterTransactions(transactions, 'captures').length,
        [transactions],
    );

    return (
        <Container className="py-4" data-testid="partner-transactions-page">
            <div className="partner-page-intro">
                <h2>Transactions</h2>
                <p>Tap a tile to open its payment list on a dedicated page. Start with exceptions and refunds.</p>
            </div>
            <ResourceAlert error={resourceErrors.transactions || resourceErrors.transactionSummary} />
            <Row>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Exceptions"
                        value={`${transactionSummary.failed_count || 0} / ${formatCurrency(transactionSummary.failed_total)}`}
                        hint="Failed, declined, or canceled events that need direct explanation."
                        tone="critical"
                        ctaLabel="Open list"
                        onClick={() => history.push('/transactions/exceptions')}
                        testId="partner-tx-card-exceptions"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Refunds"
                        value={`${transactionSummary.refund_count || 0} / ${formatCurrency(transactionSummary.refund_total)}`}
                        hint="Refund volume already visible across your merchant footprint."
                        tone="warning"
                        ctaLabel="Open list"
                        onClick={() => history.push('/transactions/refunds')}
                        testId="partner-tx-card-refunds"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Processing queue"
                        value={`${transactionSummary.processing_count || 0} / ${formatCurrency(transactionSummary.processing_total)}`}
                        hint="Events still processing or reprocessing that may raise staff questions."
                        tone="warning"
                        ctaLabel="Open list"
                        onClick={() => history.push('/transactions/processing')}
                        testId="partner-tx-card-processing"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Approved"
                        value={`${transactionSummary.approved_count || 0} / ${formatCurrency(transactionSummary.approved_total)}`}
                        hint="Approved captures, pre-auths, and refunds in the current feed."
                        tone="healthy"
                        ctaLabel="Open list"
                        onClick={() => history.push('/transactions/approved')}
                        testId="partner-tx-card-approved"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Captures"
                        value={captureCount}
                        hint="Capture events visible in the current organization feed."
                        tone="neutral"
                        ctaLabel="Open list"
                        onClick={() => history.push('/transactions/captures')}
                        testId="partner-tx-card-captures"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="All transactions"
                        value={(transactions || []).length}
                        hint="Open the full payment feed for this organization."
                        tone="neutral"
                        ctaLabel="Open list"
                        onClick={() => history.push('/transactions/all')}
                        testId="partner-tx-card-all"
                    />
                </Col>
            </Row>
        </Container>
    );
}

function TransactionsDetailPage({ transactions }) {
    const { view } = useParams();
    const config = TRANSACTION_VIEWS[view];
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const filteredTransactions = useMemo(
        () => filterTransactions(transactions, config ? config.filter : null),
        [transactions, config],
    );

    if (!config) {
        return <Redirect to="/transactions" />;
    }

    return (
        <DetailPageChrome
            title={config.title}
            subtitle={config.subtitle}
            backTo="/transactions"
            backLabel="Back to Transactions"
            testId="partner-transactions-detail-page"
            trailing={<Badge color="secondary" pill className="px-3 py-2">{filteredTransactions.length} visible</Badge>}
        >
            <TransactionDetailPanel
                transaction={selectedTransaction}
                onClose={() => setSelectedTransaction(null)}
            />
            <Card className="partner-panel">
                <CardBody>
                    <Table responsive hover data-testid="partner-transactions-table">
                        <thead>
                            <tr>
                                <th>Created</th>
                                <th>Bill</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Amount</th>
                                <th>Provider</th>
                                <th>Provider Tx / Ref</th>
                                <th>Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTransactions.length ? filteredTransactions.map((transaction) => (
                                <tr
                                    key={transaction.id}
                                    className={`partner-row-action${selectedTransaction && selectedTransaction.id === transaction.id ? ' is-selected' : ''}`}
                                    onClick={() => setSelectedTransaction(transaction)}
                                >
                                    <td>{formatDateTime(transaction.created_at)}</td>
                                    <td><code>{normalizeText(transaction.bill_id)}</code></td>
                                    <td>{formatTransactionType(transaction.tx_type)}</td>
                                    <td>{normalizeText(TRANSACTION_STATUS_LABELS[getTransactionStatusCode(transaction)], 'Unknown')}</td>
                                    <td>{formatCurrency(transaction.amount)}</td>
                                    <td>{normalizeText(transaction.provider || 'payfacto')}</td>
                                    <td style={{ maxWidth: 220, wordBreak: 'break-all' }}>
                                        {normalizeText(
                                            transaction.provider_tx_id
                                            || transaction.payfacto_tx_no
                                            || transaction.payfacto_auth_no
                                            || transaction.payfacto_merchant_no,
                                            'Pending',
                                        )}
                                    </td>
                                    <td style={{ maxWidth: 240 }}>
                                        {isRefundTransaction(transaction)
                                            ? formatRefundReason(transaction)
                                            : '-'}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="8" className="text-muted">
                                        {config.filter
                                            ? 'No transactions match this filter in the current organization scope.'
                                            : 'No transactions are currently visible for this organization scope.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>
        </DetailPageChrome>
    );
}

function printSettlementStatement(financeSummary, organizationName) {
    const recentNet = getRecentNetCapturedTotal(financeSummary);
    const generatedAt = new Date().toLocaleString();
    const html = `<!doctype html><html><head><title>Settlement statement</title>
<style>
body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;color:#0f172a;padding:32px;max-width:720px;margin:0 auto}
h1{font-size:1.4rem;margin:0 0 4px} .muted{color:#64748b} table{width:100%;border-collapse:collapse;margin-top:24px}
td{padding:10px 0;border-bottom:1px solid #e2e8f0} td:last-child{text-align:right;font-variant-numeric:tabular-nums}
.note{margin-top:24px;padding:12px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;font-size:0.92rem}
@media print{button{display:none}}
</style></head><body>
<button onclick="window.print()" style="margin-bottom:16px;padding:8px 14px">Print / Save as PDF</button>
<p class="muted" style="letter-spacing:0.12em;text-transform:uppercase;font-size:0.75rem">BillMaster · Restaurant settlement statement</p>
<h1>${normalizeText(organizationName, 'Your organization')}</h1>
<p class="muted">Generated ${generatedAt}. Estimate-based from partner finance summary — not a tax invoice.</p>
<table>
<tr><td>Net captured</td><td>${formatCurrency(financeSummary.net_captured_total)}</td></tr>
<tr><td>Last ${financeSummary.recent_window_days || 7} days (net)</td><td>${formatCurrency(recentNet)}</td></tr>
<tr><td>Approved captures</td><td>${financeSummary.approved_capture_count || 0} / ${formatCurrency(financeSummary.approved_capture_total)}</td></tr>
<tr><td>Refunds</td><td>${financeSummary.refund_count || 0} / ${formatCurrency(financeSummary.refund_total)}</td></tr>
<tr><td>Est. platform revenue</td><td>${formatCurrency(financeSummary.estimated_bm_total_revenue)}</td></tr>
<tr><td>Est. restaurant payout (after txn fees)</td><td>${formatCurrency(financeSummary.estimated_restaurant_payout_after_transaction_fees)}</td></tr>
<tr><td>Settlement health</td><td>${formatSettlementHealth(financeSummary.settlement_health)}</td></tr>
<tr><td>Last successful capture</td><td>${formatDateTime(financeSummary.last_successful_capture_at)}</td></tr>
</table>
<div class="note">${normalizeText(financeSummary.fee_reporting_note, 'Fee figures are estimate-based from current commercial terms and payment events.')}</div>
</body></html>`;
    const win = window.open('', '_blank', 'noopener,noreferrer,width=800,height=900');
    if (!win) {
        return false;
    }
    win.document.write(html);
    win.document.close();
    return true;
}

function FeesHubPage({ financeSummary, transactions, organization }) {
    const history = useHistory();
    const paymentExceptionCount = getFinancePaymentExceptionCount(financeSummary);
    const recentNetCapturedTotal = getRecentNetCapturedTotal(financeSummary);
    const financeExceptionBreakdown = buildFinanceExceptionBreakdown(financeSummary);
    const paymentExceptionSummary = financeExceptionBreakdown.length
        ? financeExceptionBreakdown
            .map((bucket) => `${bucket.label} ${bucket.count} / ${formatCurrency(bucket.total)}`)
            .join(', ')
        : 'No pending, failed, declined, or voided/canceled payment events are currently visible.';

    return (
        <Container className="py-4" data-testid="partner-fees-page">
            <div className="d-flex justify-content-between align-items-start flex-wrap mb-3">
                <div>
                    <h2 className="h5 mb-1">Fees &amp; settlements</h2>
                    <p className="mb-0 text-muted">Tap a tile to open its detail page. Download a statement anytime from the hub.</p>
                </div>
                <Button
                    color="secondary"
                    outline
                    className="mt-2 mt-md-0 partner-portal-action-btn"
                    data-testid="partner-settlement-statement-btn"
                    onClick={() => {
                        const ok = printSettlementStatement(financeSummary, organization && organization.name);
                        if (!ok) {
                            window.alert('Allow pop-ups to open the settlement statement.');
                        }
                    }}
                >
                    Download statement
                </Button>
            </div>
            <Row>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Net captured"
                        value={formatCurrency(financeSummary.net_captured_total)}
                        hint="Approved captures less refunds."
                        ctaLabel="Open snapshot"
                        onClick={() => history.push('/fees-and-settlements/snapshot')}
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label={`Last ${financeSummary.recent_window_days || 7} days`}
                        value={formatCurrency(recentNetCapturedTotal)}
                        hint="Recent settled flow after refunds."
                        ctaLabel="Open payments"
                        onClick={() => history.push('/fees-and-settlements/recent')}
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Refunds"
                        value={`${financeSummary.refund_count || 0} / ${formatCurrency(financeSummary.refund_total)}`}
                        hint="Approved refunds visible in the portal."
                        ctaLabel="Open snapshot"
                        onClick={() => history.push('/fees-and-settlements/snapshot')}
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Payment exceptions"
                        value={paymentExceptionCount}
                        hint={paymentExceptionSummary}
                        tone="warning"
                        ctaLabel="Open snapshot"
                        onClick={() => history.push('/fees-and-settlements/snapshot')}
                        testId="partner-fees-card-exceptions"
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Platform revenue est."
                        value={formatCurrency(financeSummary.estimated_bm_total_revenue)}
                        hint="Estimated platform revenue from fees and plan charges."
                        ctaLabel="Open contract"
                        onClick={() => history.push('/fees-and-settlements/contract')}
                    />
                </Col>
                <Col md="4" className="mb-3">
                    <SummaryCard
                        label="Estimated payout"
                        value={formatCurrency(financeSummary.estimated_restaurant_payout_after_transaction_fees)}
                        hint="Net captured after estimated transaction fees."
                        ctaLabel="Open contract"
                        onClick={() => history.push('/fees-and-settlements/contract')}
                    />
                </Col>
            </Row>
        </Container>
    );
}

function FeesDetailPage({ financeSummary, transactions, organization }) {
    const { view } = useParams();
    const recentTransactions = (transactions || []).slice(0, 25);
    const merchantNumbers = financeSummary.merchant_numbers || [];
    const commercialTerms = financeSummary.commercial_terms || {};
    const financeExceptionBreakdown = buildFinanceExceptionBreakdown(financeSummary);
    const paymentExceptionCount = getFinancePaymentExceptionCount(financeSummary);
    const recentNetCapturedTotal = getRecentNetCapturedTotal(financeSummary);
    const paymentExceptionSummary = financeExceptionBreakdown.length
        ? financeExceptionBreakdown
            .map((bucket) => `${bucket.label} ${bucket.count} / ${formatCurrency(bucket.total)}`)
            .join(', ')
        : 'No pending, failed, declined, or voided/canceled payment events are currently visible.';

    const titles = {
        snapshot: { title: 'Settlement snapshot', subtitle: 'What settled, what refunded, and what still needs attention.' },
        contract: { title: 'Contract & revenue', subtitle: 'Commercial terms and estimated platform revenue behind these figures.' },
        recent: { title: 'Recent payment events', subtitle: 'Latest payment events feeding settlement estimates.' },
    };
    const config = titles[view];
    if (!config) {
        return <Redirect to="/fees-and-settlements" />;
    }

    let body = null;
    if (view === 'snapshot') {
        body = (
            <Card style={{ border: '1px solid #e7e5e4' }} data-testid="partner-settlement-context-card">
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Badge color={getSettlementHealthColor(financeSummary.settlement_health)} pill className="px-3 py-2">
                            {formatSettlementHealth(financeSummary.settlement_health)}
                        </Badge>
                    </div>
                    <Alert color="secondary">
                        {normalizeText(financeSummary.fee_reporting_note, 'These fee numbers are estimate-based and driven by current payment events.')}
                    </Alert>
                    <Table responsive size="sm" className="mb-0">
                        <tbody>
                            <tr>
                                <td className="w-50"><strong>Approved captures</strong></td>
                                <td>{financeSummary.approved_capture_count || 0} / {formatCurrency(financeSummary.approved_capture_total)}</td>
                            </tr>
                            <tr>
                                <td><strong>Recent settled</strong></td>
                                <td>{formatCurrency(recentNetCapturedTotal)}</td>
                            </tr>
                            <tr>
                                <td><strong>Refunds</strong></td>
                                <td>{financeSummary.refund_count || 0} / {formatCurrency(financeSummary.refund_total)}</td>
                            </tr>
                            <tr>
                                <td><strong>Needs follow-up</strong></td>
                                <td>{paymentExceptionCount}</td>
                            </tr>
                            <tr>
                                <td><strong>Exception mix</strong></td>
                                <td>{paymentExceptionSummary}</td>
                            </tr>
                            <tr>
                                <td><strong>Merchant numbers</strong></td>
                                <td>{merchantNumbers.length ? merchantNumbers.join(', ') : 'Pending'}</td>
                            </tr>
                            <tr>
                                <td><strong>Last successful payment</strong></td>
                                <td>{formatDateTime(financeSummary.last_successful_capture_at)}</td>
                            </tr>
                            <tr>
                                <td><strong>Latest finalized bill</strong></td>
                                <td>{formatDateTime(financeSummary.last_finalized_bill_at)}</td>
                            </tr>
                        </tbody>
                    </Table>
                </CardBody>
            </Card>
        );
    } else if (view === 'contract') {
        body = (
            <Card style={{ border: '1px solid #e7e5e4' }} data-testid="partner-fees-commercial-card">
                <CardBody>
                    <Badge color="secondary" pill className="px-3 py-2 mb-3">
                        {normalizeText(financeSummary.fee_reporting_status)}
                    </Badge>
                    <div className="mb-2"><strong>Digital fee:</strong> {formatBps(commercialTerms.digital_fee_bps)}</div>
                    <div className="mb-2"><strong>Processor fee estimate:</strong> {formatBps(commercialTerms.processor_fee_bps_estimate)}</div>
                    <div className="mb-2"><strong>PayFacto rev-share:</strong> {formatBps(commercialTerms.payfacto_rev_share_bps)}</div>
                    <div className="mb-2"><strong>Plan:</strong> {normalizeText(commercialTerms.premium_feature_label, 'Not configured')}</div>
                    <div><strong>Plan fee:</strong> {formatCurrency(commercialTerms.premium_feature_fee)}</div>
                    <hr />
                    <div data-testid="partner-fees-revenue-card">
                        <h2 className="h5 mb-3">Revenue estimate</h2>
                        <Table responsive hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Line item</th>
                                    <th>Current period</th>
                                    <th>Last {financeSummary.recent_window_days || 7} days</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Digital fee</td>
                                    <td>{formatCurrency(financeSummary.estimated_digital_fee_net_total)}</td>
                                    <td>{formatCurrency(financeSummary.recent_estimated_digital_fee_total)}</td>
                                </tr>
                                <tr>
                                    <td>Processor fee est.</td>
                                    <td>{formatCurrency(financeSummary.estimated_processor_fee_net_total)}</td>
                                    <td>{formatCurrency(financeSummary.recent_estimated_processor_fee_total)}</td>
                                </tr>
                                <tr>
                                    <td>PayFacto rev-share</td>
                                    <td>{formatCurrency(financeSummary.estimated_payfacto_rev_share_net_total)}</td>
                                    <td>{formatCurrency(financeSummary.recent_estimated_payfacto_rev_share_total)}</td>
                                </tr>
                                <tr>
                                    <td>{normalizeText(commercialTerms.premium_feature_label, 'Premium feature')}</td>
                                    <td>{formatCurrency(financeSummary.estimated_bm_subscription_total)}</td>
                                    <td>{formatCurrency(financeSummary.estimated_bm_subscription_total)}</td>
                                </tr>
                                <tr>
                                    <td><strong>Platform revenue total</strong></td>
                                    <td><strong>{formatCurrency(financeSummary.estimated_bm_total_revenue)}</strong></td>
                                    <td><strong>{formatCurrency(financeSummary.recent_estimated_digital_fee_total + financeSummary.recent_estimated_payfacto_rev_share_total + (financeSummary.estimated_bm_subscription_total || 0))}</strong></td>
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
            </Card>
        );
    } else if (view === 'recent') {
        body = (
            <Card style={{ border: '1px solid #e7e5e4' }} data-testid="partner-fees-transactions-card">
                <CardBody>
                    <Table responsive hover className="mb-0" data-testid="partner-fees-transactions-table">
                        <thead>
                            <tr>
                                <th>Created</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Amount</th>
                                <th>Provider</th>
                                <th>Provider Tx</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentTransactions.length ? recentTransactions.map((transaction) => (
                                <tr key={transaction.id}>
                                    <td>{formatDateTime(transaction.created_at)}</td>
                                    <td>{formatTransactionType(transaction.tx_type)}</td>
                                    <td>{normalizeText(TRANSACTION_STATUS_LABELS[transaction.tx_status], 'Unknown')}</td>
                                    <td>{formatCurrency(transaction.amount)}</td>
                                    <td>{normalizeText(transaction.provider || 'payfacto')}</td>
                                    <td style={{ maxWidth: 220, wordBreak: 'break-all' }}>
                                        {normalizeText(
                                            transaction.provider_tx_id
                                            || transaction.payfacto_tx_no
                                            || transaction.payfacto_auth_no,
                                            'Pending',
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="6" className="text-muted">No recent payment events.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>
        );
    }

    return (
        <DetailPageChrome
            title={config.title}
            subtitle={config.subtitle}
            backTo="/fees-and-settlements"
            backLabel="Back to Fees"
            testId="partner-fees-detail-page"
        >
            {body}
        </DetailPageChrome>
    );
}

function buildLoyaltyContext(loyaltySummary, rewardTransactions) {
    const paymentModeSummary = loyaltySummary.payment_mode_summary || [];
    const topAccounts = loyaltySummary.top_accounts || [];
    const rollingWindowDays = loyaltySummary.rolling_window_days || 30;
    const accountCount = loyaltySummary.account_count || 0;
    const enabledAccountCount = loyaltySummary.enabled_account_count || 0;
    const inactiveAccountCount = Math.max(accountCount - enabledAccountCount, 0);
    const recentActivityCount = loyaltySummary.recent_activity_count || 0;
    const pendingAwardsTotal = loyaltySummary.pending_amount_total || 0;
    const loyaltyHealth = recentActivityCount === 0
        ? { color: 'warning', label: 'Needs attention' }
        : inactiveAccountCount > 0 || pendingAwardsTotal > 0
            ? { color: 'secondary', label: 'Monitor' }
            : { color: 'success', label: 'Healthy' };
    const leadPaymentMode = paymentModeSummary.length
        ? [...paymentModeSummary].sort((left, right) => Number(right.balance_total || 0) - Number(left.balance_total || 0))[0]
        : null;
    return {
        paymentModeSummary,
        topAccounts,
        rollingWindowDays,
        accountCount,
        enabledAccountCount,
        inactiveAccountCount,
        recentActivityCount,
        pendingAwardsTotal,
        loyaltyHealth,
        leadPaymentMode,
        rewardTransactions: rewardTransactions || [],
    };
}

function LoyaltyHubPage({ loyaltySummary, rewardTransactions, resourceErrors = {} }) {
    const history = useHistory();
    const ctx = buildLoyaltyContext(loyaltySummary, rewardTransactions);

    return (
        <Container className="py-4" data-testid="partner-loyalty-page">
            <ResourceAlert error={resourceErrors.loyaltySummary || resourceErrors.rewardTransactions} />
            <div className="partner-page-intro mb-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center" style={{ gap: 12 }}>
                    <div>
                        <h2 className="h4 mb-1">Loyalty</h2>
                        <p className="mb-0 text-muted">Tap a tile to open its detail page. Keep the hub light and jump into the list you need.</p>
                    </div>
                    <Badge color={ctx.loyaltyHealth.color} pill className="px-3 py-2">
                        {ctx.loyaltyHealth.label}
                    </Badge>
                </div>
            </div>
            <Row>
                <Col md="3" className="mb-3">
                    <OperationalSignalCard
                        label="Live accounts"
                        value={`${ctx.enabledAccountCount} / ${ctx.accountCount}`}
                        hint="How much of the visible member base can use loyalty right now."
                        ctaLabel="Open detail"
                        onClick={() => history.push('/loyalty/accounts')}
                        testId="partner-loyalty-card-accounts"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <OperationalSignalCard
                        label="Pending awards"
                        value={formatCurrency(ctx.pendingAwardsTotal)}
                        hint="Awarded value not yet cleared into live balances."
                        ctaLabel="Open detail"
                        onClick={() => history.push('/loyalty/pending')}
                        testId="partner-loyalty-card-pending"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <OperationalSignalCard
                        label="Recent activity"
                        value={ctx.recentActivityCount}
                        hint={`Visible loyalty events in the last ${ctx.rollingWindowDays} days.`}
                        ctaLabel="Open detail"
                        onClick={() => history.push('/loyalty/activity')}
                        testId="partner-loyalty-card-activity"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <OperationalSignalCard
                        label="Needs attention"
                        value={ctx.inactiveAccountCount}
                        hint="Inactive accounts and other follow-up signals."
                        ctaLabel="Open detail"
                        onClick={() => history.push('/loyalty/attention')}
                        testId="partner-loyalty-card-attention"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Members"
                        value={ctx.accountCount}
                        hint="Visible loyalty accounts in your restaurant group."
                        ctaLabel="Open detail"
                        onClick={() => history.push('/loyalty/members')}
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Open balance"
                        value={formatCurrency(loyaltySummary.balance_total)}
                        hint="Outstanding loyalty value still sitting on member accounts."
                        ctaLabel="Open detail"
                        onClick={() => history.push('/loyalty/balances')}
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Total earned"
                        value={formatCurrency(loyaltySummary.total_earned)}
                        hint="Cumulative loyalty value awarded through the program."
                        ctaLabel="Open detail"
                        onClick={() => history.push('/loyalty/totals')}
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Redeemed"
                        value={formatCurrency(loyaltySummary.redemption_total)}
                        hint="Loyalty value already used at your restaurant."
                        ctaLabel="Open detail"
                        onClick={() => history.push('/loyalty/totals')}
                    />
                </Col>
            </Row>
        </Container>
    );
}

function LoyaltyDetailPage({ loyaltySummary, rewardTransactions }) {
    const { view } = useParams();
    const ctx = buildLoyaltyContext(loyaltySummary, rewardTransactions);
    const titles = {
        accounts: { title: 'Live accounts', subtitle: 'Enabled vs total loyalty accounts in your restaurant group.' },
        pending: { title: 'Pending awards', subtitle: 'Awarded value not yet cleared into live balances.' },
        activity: { title: 'Recent loyalty activity', subtitle: 'The live event stream operators can use to confirm what changed and when.' },
        attention: { title: 'Needs attention', subtitle: 'Operational checklist for loyalty follow-up.' },
        members: { title: 'Largest member balances', subtitle: 'Supporting context for outreach and account review.' },
        balances: { title: 'Open balances', subtitle: 'Outstanding loyalty value still sitting on member accounts.' },
        totals: { title: 'Program totals', subtitle: 'Reference totals for how loyalty has performed over time.' },
    };
    const config = titles[view];
    if (!config) {
        return <Redirect to="/loyalty" />;
    }

    let body = null;
    if (view === 'attention' || view === 'pending' || view === 'accounts') {
        body = (
            <Card style={{ border: '1px solid #e7e5e4' }} data-testid="partner-loyalty-mix-card">
                <CardBody>
                    <Table responsive size="sm" className="mb-0">
                        <tbody>
                            <tr>
                                <td className="w-50"><strong>Live accounts</strong></td>
                                <td>{ctx.enabledAccountCount} / {ctx.accountCount}</td>
                            </tr>
                            <tr>
                                <td><strong>Inactive accounts</strong></td>
                                <td>{ctx.inactiveAccountCount}</td>
                            </tr>
                            <tr>
                                <td><strong>Pending awards</strong></td>
                                <td>{formatCurrency(ctx.pendingAwardsTotal)}</td>
                            </tr>
                            <tr>
                                <td><strong>Recent activity</strong></td>
                                <td>{ctx.recentActivityCount} events</td>
                            </tr>
                            <tr>
                                <td><strong>Last activity</strong></td>
                                <td>{formatDateTime(loyaltySummary.last_activity_at)}</td>
                            </tr>
                            <tr>
                                <td><strong>Primary payment mode</strong></td>
                                <td>{ctx.leadPaymentMode ? formatPaymentMode(ctx.leadPaymentMode.payment_mode) : 'Not available yet'}</td>
                            </tr>
                        </tbody>
                    </Table>
                </CardBody>
            </Card>
        );
    } else if (view === 'totals' || view === 'balances') {
        body = (
            <Card style={{ border: '1px solid #e7e5e4' }} data-testid="partner-loyalty-payment-mode-card">
                <CardBody>
                    <Table responsive hover className="mb-0">
                        <thead>
                            <tr>
                                <th>Line item</th>
                                <th>All time</th>
                                <th>Last {ctx.rollingWindowDays} days</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Open balance</td>
                                <td colSpan="2">{formatCurrency(loyaltySummary.balance_total)}</td>
                            </tr>
                            <tr>
                                <td>Awards</td>
                                <td>{(loyaltySummary.award_count || 0)} / {formatCurrency(loyaltySummary.award_total)}</td>
                                <td>{formatCurrency(loyaltySummary.recent_award_total)}</td>
                            </tr>
                            <tr>
                                <td>Redemptions</td>
                                <td>{(loyaltySummary.redemption_count || 0)} / {formatCurrency(loyaltySummary.redemption_total)}</td>
                                <td>{formatCurrency(loyaltySummary.recent_redemption_total)}</td>
                            </tr>
                            <tr>
                                <td>Adjustments</td>
                                <td>{(loyaltySummary.adjustment_count || 0)} / {formatCurrency(loyaltySummary.adjustment_total)}</td>
                                <td>{formatCurrency(loyaltySummary.recent_adjustment_total)}</td>
                            </tr>
                        </tbody>
                    </Table>
                </CardBody>
            </Card>
        );
    } else if (view === 'members') {
        body = (
            <Card style={{ border: '1px solid #e7e5e4' }} data-testid="partner-loyalty-accounts-card">
                <CardBody>
                    <Table responsive hover className="mb-0">
                        <thead>
                            <tr>
                                <th>Account</th>
                                <th>Mode</th>
                                <th>Balance</th>
                                <th>Pending</th>
                                <th>Last activity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ctx.topAccounts.length ? ctx.topAccounts.map((account) => (
                                <tr key={account.id}>
                                    <td><code>{account.id}</code></td>
                                    <td>{formatPaymentMode(account.payment_mode)}</td>
                                    <td>{formatCurrency(account.balance)}</td>
                                    <td>{formatCurrency(account.pending_amount)}</td>
                                    <td>{formatDateTime(account.last_activity_at)}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="text-muted">No reward-account detail is visible for this organization yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>
        );
    } else if (view === 'activity') {
        body = (
            <Card style={{ border: '1px solid #e7e5e4' }}>
                <CardBody>
                    <Table responsive hover className="mb-0" data-testid="partner-loyalty-table">
                        <thead>
                            <tr>
                                <th>Created</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Spend progress</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ctx.rewardTransactions.length ? ctx.rewardTransactions.map((transaction) => (
                                <tr key={transaction.id}>
                                    <td>{formatDateTime(transaction.created_at)}</td>
                                    <td>{formatRewardTransactionType(transaction.tx_type)}</td>
                                    <td>{formatCurrency(transaction.amount)}</td>
                                    <td>{formatCurrency(transaction.spend_progress)}</td>
                                    <td>{normalizeText(transaction.description, 'No description')}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="text-muted">No loyalty activity is currently visible for this organization scope.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>
        );
    }

    return (
        <DetailPageChrome
            title={config.title}
            subtitle={config.subtitle}
            backTo="/loyalty"
            backLabel="Back to Loyalty"
            testId="partner-loyalty-detail-page"
        >
            {body}
        </DetailPageChrome>
    );
}

const SUPPORT_CATEGORIES = [
    {
        id: 'guest-payment',
        label: 'Guest payment help',
        guidance: 'Check Transactions for the guest bill, confirm provider/tx id, then retry or explain the decline at the table.',
    },
    {
        id: 'table-seat',
        label: 'Table / seat assignment',
        guidance: 'Confirm the guest QR/table mapping in Activity, reseat if needed, and ask the guest to refresh the App Clip.',
    },
    {
        id: 'bill-question',
        label: 'Bill or tip question',
        guidance: 'Open the bill in Activity, walk the guest through line items and tip, and capture notes before closing.',
    },
    {
        id: 'loyalty',
        label: 'Loyalty / rewards',
        guidance: 'Use Loyalty to verify account balance and recent reward events before adjusting guest expectations.',
    },
    {
        id: 'escalate-billmaster',
        label: 'Escalate to BillMaster',
        guidance: 'Use only for platform outages, settlement disputes, or cases your team cannot resolve on-site.',
    },
];

function buildSupportSummary(supportRequests) {
    const activeStatuses = new Set(['new', 'acknowledged', 'assigned', 'contacted', 'nurturing']);
    const now = Date.now();
    const byStatus = supportRequests.reduce((accumulator, requestItem) => {
        const key = normalizeText(requestItem.status, 'new').toLowerCase();
        accumulator[key] = (accumulator[key] || 0) + 1;
        return accumulator;
    }, {});
    const unassignedCount = supportRequests.filter((requestItem) => !requestItem.assignee_name).length;
    const activeCount = supportRequests.filter((requestItem) => activeStatuses.has(normalizeText(requestItem.status, 'new').toLowerCase())).length;
    const followUpCount = supportRequests.filter((requestItem) => {
        if (!requestItem.next_follow_up_at) {
            return false;
        }
        const followUpAt = Number(requestItem.next_follow_up_at) < 1e12 ? Number(requestItem.next_follow_up_at) * 1000 : Number(requestItem.next_follow_up_at);
        return Number.isFinite(followUpAt) && followUpAt <= now;
    }).length;
    const recentlyActive = [...supportRequests]
        .sort((left, right) => Number(right.last_activity_at || right.created_at || 0) - Number(left.last_activity_at || left.created_at || 0))
        .slice(0, 6);
    const locationBreakdown = Object.values(supportRequests.reduce((accumulator, requestItem) => {
        const key = requestItem.location_id || 'organization';
        if (!accumulator[key]) {
            accumulator[key] = {
                key,
                label: normalizeText(requestItem.location_name, 'Organization-wide'),
                count: 0,
                active_count: 0,
            };
        }
        accumulator[key].count += 1;
        if (activeStatuses.has(normalizeText(requestItem.status, 'new').toLowerCase())) {
            accumulator[key].active_count += 1;
        }
        return accumulator;
    }, {})).sort((left, right) => right.count - left.count);

    return {
        byStatus,
        unassignedCount,
        activeCount,
        followUpCount,
        recentlyActive,
        locationBreakdown,
        lastActivityAt: recentlyActive[0] ? (recentlyActive[0].last_activity_at || recentlyActive[0].created_at) : null,
        activeStatuses,
    };
}

function filterSupportRequests(supportRequests, view) {
    const summary = buildSupportSummary(supportRequests);
    const now = Date.now();
    switch (view) {
        case 'active':
            return supportRequests.filter((requestItem) => summary.activeStatuses.has(normalizeText(requestItem.status, 'new').toLowerCase()));
        case 'unassigned':
            return supportRequests.filter((requestItem) => !requestItem.assignee_name);
        case 'follow-up':
            return supportRequests.filter((requestItem) => {
                if (!requestItem.next_follow_up_at) return false;
                const followUpAt = Number(requestItem.next_follow_up_at) < 1e12 ? Number(requestItem.next_follow_up_at) * 1000 : Number(requestItem.next_follow_up_at);
                return Number.isFinite(followUpAt) && followUpAt <= now;
            });
        case 'recent':
            return summary.recentlyActive;
        case 'all':
        default:
            return supportRequests;
    }
}

function SupportHubPage({ supportRequests, locations, onCreateSupportRequest, pending, error }) {
    const history = useHistory();
    const supportSummary = useMemo(() => buildSupportSummary(supportRequests), [supportRequests]);

    return (
        <Container className="py-4" data-testid="partner-support-page">
            <Alert color="secondary" className="mb-3" data-testid="partner-support-tier1-banner">
                <strong>Tier-1 first:</strong> resolve guest friction at the restaurant using Activity and Transactions.
                Escalate to BillMaster only for platform or settlement special cases.
            </Alert>
            <div className="partner-page-intro mb-3">
                <h2 className="h4 mb-1">Support</h2>
                <p className="mb-0 text-muted">Tap a tile to open its issue list or the intake form on a dedicated page.</p>
            </div>
            <Row>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Visible issues"
                        value={supportRequests.length}
                        hint="All support requests currently visible to this organization."
                        ctaLabel="Open list"
                        onClick={() => history.push('/support/all')}
                        testId="partner-support-card-all"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Active issues"
                        value={supportSummary.activeCount}
                        hint="Requests still in a live support state and likely to matter operationally."
                        tone="warning"
                        ctaLabel="Open list"
                        onClick={() => history.push('/support/active')}
                        testId="partner-support-card-active"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Unassigned"
                        value={supportSummary.unassignedCount}
                        hint="Issues without a named restaurant owner yet."
                        tone="warning"
                        ctaLabel="Open list"
                        onClick={() => history.push('/support/unassigned')}
                        testId="partner-support-card-unassigned"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Follow-up due"
                        value={supportSummary.followUpCount}
                        hint="Requests whose next follow-up time is due or overdue."
                        tone="critical"
                        ctaLabel="Open list"
                        onClick={() => history.push('/support/follow-up')}
                        testId="partner-support-card-follow-up"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Log an issue"
                        value="New"
                        hint="Capture a guest or floor issue for your restaurant team."
                        tone="healthy"
                        ctaLabel="Open form"
                        onClick={() => history.push('/support/new')}
                        testId="partner-support-card-new"
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="Recently active"
                        value={supportSummary.recentlyActive.length}
                        hint="Issues with the newest movement across your organization."
                        ctaLabel="Open list"
                        onClick={() => history.push('/support/recent')}
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <SummaryCard
                        label="By location"
                        value={supportSummary.locationBreakdown.length}
                        hint="Where issues are concentrating across your locations."
                        ctaLabel="Open detail"
                        onClick={() => history.push('/support/locations')}
                    />
                </Col>
            </Row>
        </Container>
    );
}

function SupportDetailPage({ supportRequests, locations, onCreateSupportRequest, pending, error }) {
    const { view } = useParams();
    const history = useHistory();
    const supportSummary = useMemo(() => buildSupportSummary(supportRequests), [supportRequests]);
    const [category, setCategory] = useState(SUPPORT_CATEGORIES[0].id);
    const [subject, setSubject] = useState('');
    const [locationId, setLocationId] = useState('');
    const [message, setMessage] = useState('');
    const selectedCategory = SUPPORT_CATEGORIES.find((item) => item.id === category) || SUPPORT_CATEGORIES[0];

    const titles = {
        all: { title: 'All support issues', subtitle: 'Partner-visible issue list scoped to your organization.' },
        active: { title: 'Active issues', subtitle: 'Requests still in a live support state.' },
        unassigned: { title: 'Unassigned issues', subtitle: 'Issues without a named restaurant owner yet.' },
        'follow-up': { title: 'Follow-up due', subtitle: 'Requests whose next follow-up time is due or overdue.' },
        recent: { title: 'Recently active issues', subtitle: 'Newest movement across your organization issues.' },
        locations: { title: 'Issue concentration', subtitle: 'Where issues are concentrating across your locations.' },
        new: { title: 'Log a guest / floor issue', subtitle: 'Keep an internal trail for your team. Most in-restaurant issues should stay here.' },
        posture: { title: 'Response posture', subtitle: 'Operational summary of what is open, assigned, and moving.' },
    };
    const config = titles[view];

    const submit = useCallback((event) => {
        event.preventDefault();
        const categoryLabel = selectedCategory.label;
        const composedSubject = subject.trim().toLowerCase().startsWith(categoryLabel.toLowerCase())
            ? subject.trim()
            : `[${categoryLabel}] ${subject.trim()}`;
        onCreateSupportRequest({
            subject: composedSubject,
            location_id: locationId || null,
            message: `${message.trim()}\n\n---\nTier-1 category: ${categoryLabel}\nGuidance used: ${selectedCategory.guidance}`,
        }).then((didCreate) => {
            if (didCreate) {
                setSubject('');
                setLocationId('');
                setMessage('');
                setCategory(SUPPORT_CATEGORIES[0].id);
                history.push('/support/all');
            }
        });
    }, [history, locationId, message, onCreateSupportRequest, selectedCategory, subject]);

    if (!config) {
        return <Redirect to="/support" />;
    }

    if (view === 'new') {
        return (
            <DetailPageChrome
                title={config.title}
                subtitle={config.subtitle}
                backTo="/support"
                backLabel="Back to Support"
                testId="partner-support-new-page"
            >
                <Card style={{ border: '1px solid #e7e5e4' }} data-testid="partner-support-form-card">
                    <CardBody>
                        {error ? <Alert color="danger">{error}</Alert> : null}
                        <Form onSubmit={submit} data-testid="partner-support-form">
                            <FormGroup>
                                <Label for="support-category">Issue type</Label>
                                <Input
                                    id="support-category"
                                    type="select"
                                    value={category}
                                    onChange={(event) => setCategory(event.target.value)}
                                >
                                    {SUPPORT_CATEGORIES.map((item) => (
                                        <option value={item.id} key={item.id}>{item.label}</option>
                                    ))}
                                </Input>
                                <small className="form-text text-muted">{selectedCategory.guidance}</small>
                            </FormGroup>
                            <FormGroup>
                                <Label for="support-subject">Subject</Label>
                                <Input id="support-subject" value={subject} onChange={(event) => setSubject(event.target.value)} required />
                            </FormGroup>
                            <FormGroup>
                                <Label for="support-location">Location</Label>
                                <Input id="support-location" type="select" value={locationId} onChange={(event) => setLocationId(event.target.value)}>
                                    <option value="">Organization-wide issue</option>
                                    {locations.map((location) => (
                                        <option value={location.id} key={location.id}>{normalizeText(location.name)}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label for="support-message">What happened / what you tried</Label>
                                <Input id="support-message" type="textarea" rows="6" value={message} onChange={(event) => setMessage(event.target.value)} required />
                            </FormGroup>
                            <Button color="primary" block disabled={pending}>
                                {pending
                                    ? 'Submitting...'
                                    : (category === 'escalate-billmaster' ? 'Escalate to BillMaster' : 'Save for restaurant team')}
                            </Button>
                        </Form>
                    </CardBody>
                </Card>
            </DetailPageChrome>
        );
    }

    if (view === 'locations') {
        return (
            <DetailPageChrome
                title={config.title}
                subtitle={config.subtitle}
                backTo="/support"
                backLabel="Back to Support"
                testId="partner-support-locations-page"
            >
                <Card style={{ border: '1px solid #e7e5e4' }} data-testid="partner-support-location-card">
                    <CardBody>
                        <Table responsive hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Scope</th>
                                    <th>Total</th>
                                    <th>Active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {supportSummary.locationBreakdown.map((locationItem) => (
                                    <tr key={locationItem.key}>
                                        <td>{locationItem.label}</td>
                                        <td>{locationItem.count}</td>
                                        <td>{locationItem.active_count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>
            </DetailPageChrome>
        );
    }

    if (view === 'posture') {
        return (
            <DetailPageChrome
                title={config.title}
                subtitle={config.subtitle}
                backTo="/support"
                backLabel="Back to Support"
                testId="partner-support-posture-page"
            >
                <Card style={{ border: '1px solid #e7e5e4' }} data-testid="partner-support-summary-card">
                    <CardBody>
                        <Badge color="secondary" pill className="px-3 py-2 mb-3">Last activity {formatDateTime(supportSummary.lastActivityAt)}</Badge>
                        <Row>
                            <Col md="6">
                                <div className="mb-2"><strong>New:</strong> {supportSummary.byStatus.new || 0}</div>
                                <div className="mb-2"><strong>Acknowledged:</strong> {supportSummary.byStatus.acknowledged || 0}</div>
                                <div className="mb-2"><strong>Assigned:</strong> {supportSummary.byStatus.assigned || 0}</div>
                                <div><strong>Contacted:</strong> {supportSummary.byStatus.contacted || 0}</div>
                            </Col>
                            <Col md="6">
                                <div className="mb-2"><strong>Nurturing:</strong> {supportSummary.byStatus.nurturing || 0}</div>
                                <div className="mb-2"><strong>Closed:</strong> {supportSummary.byStatus.closed || 0}</div>
                                <div className="mb-2"><strong>Won:</strong> {supportSummary.byStatus.won || 0}</div>
                                <div><strong>Lost:</strong> {supportSummary.byStatus.lost || 0}</div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </DetailPageChrome>
        );
    }

    const rows = filterSupportRequests(supportRequests, view);
    return (
        <DetailPageChrome
            title={config.title}
            subtitle={config.subtitle}
            backTo="/support"
            backLabel="Back to Support"
            testId="partner-support-detail-page"
            trailing={<Badge color="secondary" pill className="px-3 py-2">{rows.length} visible</Badge>}
        >
            <Card style={{ border: '1px solid #e7e5e4' }} data-testid="partner-support-history-card">
                <CardBody>
                    <Table responsive hover className="mb-0" data-testid="partner-support-table">
                        <thead>
                            <tr>
                                <th>Created</th>
                                <th>Subject</th>
                                <th>Status</th>
                                <th>Location</th>
                                <th>Assignee</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length ? rows.map((requestItem) => (
                                <tr key={requestItem.id}>
                                    <td>{formatDateTime(requestItem.created_at)}</td>
                                    <td>
                                        <div>{normalizeText(requestItem.subject, 'Support issue')}</div>
                                        <div className="text-muted">{normalizeText(requestItem.message, '').slice(0, 100)}</div>
                                    </td>
                                    <td><Badge color={getSupportStatusColor(requestItem.status)}>{formatSupportStatus(requestItem.status)}</Badge></td>
                                    <td>{normalizeText(requestItem.location_name, 'Organization-wide')}</td>
                                    <td>{normalizeText(requestItem.assignee_name, 'Unassigned')}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="text-muted">No matching support issues in this view.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>
        </DetailPageChrome>
    );
}

async function requestPartnerPasswordHelp({ email, restaurantName }) {
    const response = await fetch(`${getPublicApiOrigin()}/site/contact-requests`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name: restaurantName || email,
            email,
            locale: 'en',
            source: 'partner-portal-password-help',
            message: [
                'Partner portal password help request',
                `Email: ${email}`,
                restaurantName ? `Restaurant: ${restaurantName}` : null,
                'Owner/manager cannot sign in and needs a password reset.',
            ].filter(Boolean).join('\n'),
        }),
    });

    if (!response.ok) {
        let message = 'Unable to submit the password help request.';
        try {
            const payload = await response.json();
            if (payload && payload.message) {
                message = payload.message;
            }
        } catch (parseError) {
            // Keep the generic message.
        }
        throw new Error(message);
    }

    return true;
}

function PartnerLogin({ onLogin, pending, error }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [mode, setMode] = useState('login');
    const [restaurantName, setRestaurantName] = useState('');
    const [helpPending, setHelpPending] = useState(false);
    const [helpError, setHelpError] = useState('');
    const [helpSent, setHelpSent] = useState(false);

    const submit = useCallback((event) => {
        event.preventDefault();
        onLogin(email, password);
    }, [email, onLogin, password]);

    const submitPasswordHelp = useCallback(async (event) => {
        event.preventDefault();
        setHelpPending(true);
        setHelpError('');
        setHelpSent(false);
        try {
            await requestPartnerPasswordHelp({ email, restaurantName });
            setHelpSent(true);
        } catch (requestError) {
            setHelpError(requestError.message || 'Unable to submit the password help request.');
        } finally {
            setHelpPending(false);
        }
    }, [email, restaurantName]);

    return (
        <div className="partner-login-shell" data-testid="partner-login-page">
            <div className="partner-login-glow" aria-hidden="true" />
            <main className="partner-login-main">
                <img
                    src={`${process.env.PUBLIC_URL || ''}/assets/logo_white.png`}
                    alt="BillMaster"
                    className="partner-login-logo"
                />
                <div className="partner-login-panel partner-login-glass">
                    <p className="partner-login-kicker">Restaurant workspace</p>
                    <h1 className="partner-login-title">
                        {mode === 'help' ? 'Password help' : 'Owner portal'}
                    </h1>
                    <p className="partner-login-copy">
                        {mode === 'help'
                            ? 'Owners cannot reset passwords themselves yet. Send a request and BillMaster will help you regain access.'
                            : 'Bills, payments, loyalty, and guest support for your floor.'}
                    </p>
                    {mode === 'login' && error ? (
                        <Alert color="danger" className="partner-login-error">{error}</Alert>
                    ) : null}
                    {mode === 'help' && helpError ? (
                        <Alert color="danger" className="partner-login-error">{helpError}</Alert>
                    ) : null}
                    {mode === 'help' && helpSent ? (
                        <Alert color="secondary" className="partner-login-error">
                            Request received. BillMaster will follow up at {email || 'your email'} to reset access.
                        </Alert>
                    ) : null}
                    {mode === 'login' ? (
                        <Form onSubmit={submit} className="partner-login-form">
                            <FormGroup>
                                <Label for="partner-email">Email</Label>
                                <Input
                                    id="partner-email"
                                    type="email"
                                    autoComplete="username"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="partner-password">Password</Label>
                                <Input
                                    id="partner-password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}
                                    required
                                />
                            </FormGroup>
                            <Button color="secondary" block className="partner-login-submit" disabled={pending}>
                                {pending ? 'Signing in...' : 'Enter portal'}
                            </Button>
                            <button
                                type="button"
                                className="partner-login-help-link"
                                data-testid="partner-forgot-password-link"
                                onClick={() => {
                                    setMode('help');
                                    setHelpError('');
                                    setHelpSent(false);
                                }}
                            >
                                Forgot password?
                            </button>
                        </Form>
                    ) : (
                        <Form onSubmit={submitPasswordHelp} className="partner-login-form">
                            <FormGroup>
                                <Label for="partner-help-email">Account email</Label>
                                <Input
                                    id="partner-help-email"
                                    type="email"
                                    autoComplete="username"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="partner-help-restaurant">Restaurant name</Label>
                                <Input
                                    id="partner-help-restaurant"
                                    type="text"
                                    value={restaurantName}
                                    onChange={(event) => setRestaurantName(event.target.value)}
                                    placeholder="Optional"
                                />
                            </FormGroup>
                            <Button
                                color="secondary"
                                block
                                className="partner-login-submit"
                                disabled={helpPending || helpSent}
                                data-testid="partner-password-help-submit"
                            >
                                {helpPending ? 'Sending...' : helpSent ? 'Request sent' : 'Request password help'}
                            </Button>
                            <button
                                type="button"
                                className="partner-login-help-link"
                                onClick={() => setMode('login')}
                            >
                                Back to sign in
                            </button>
                        </Form>
                    )}
                </div>
                <p className="partner-login-foot">Separate from the BillMaster admin backoffice.</p>
            </main>
        </div>
    );
}

function MenuPage({ token, locations }) {
    const api = useMemo(() => {
        const base = (locationId) => `/organizations/my/locations/${locationId}/menu-catalog`;
        return {
            listLocations: async () => locations || [],
            getCatalog: (locationId) => partnerGet(base(locationId), token),
            saveDraft: (locationId, draft, sourceUrl) =>
                partnerPut(base(locationId), token, { draft, source_url: sourceUrl || null }),
            importFromUrl: (locationId, url) =>
                partnerPost(`${base(locationId)}/import`, token, { url }),
            publish: (locationId, force) =>
                partnerPost(`${base(locationId)}/publish`, token, { force: !!force }),
            resync: (locationId, url) =>
                partnerPost(`${base(locationId)}/resync`, token, url ? { url } : {}),
        };
    }, [token, locations]);

    return (
        <Container className="py-4" data-testid="partner-menu-page">
            <MenuCatalogManager api={api} title="Restaurant menu" />
        </Container>
    );
}

function PortalLayout({ data, onLogout, isLoggingOut, token }) {
    const summary = useMemo(() => summarizeBills(data.bills), [data.bills]);

    return (
        <div className="partner-portal-shell">
            <HeroHeader
                organization={data.organization}
                locations={data.locations}
                profile={data.profile}
                onLogout={onLogout}
                isLoggingOut={isLoggingOut}
            />
            <PortalNav />
            <Switch>
                <Route exact path="/overview">
                    <OverviewPage organization={data.organization} locations={data.locations} summary={summary} />
                </Route>
                <Route exact path="/activity">
                    <ActivityHubPage bills={data.bills} locations={data.locations} transactions={data.transactions} />
                </Route>
                <Route path="/activity/:view">
                    <ActivityDetailPage bills={data.bills} transactions={data.transactions} />
                </Route>
                <Route exact path="/transactions">
                    <TransactionsHubPage transactions={data.transactions} transactionSummary={data.transactionSummary} resourceErrors={data.resourceErrors} />
                </Route>
                <Route path="/transactions/:view">
                    <TransactionsDetailPage transactions={data.transactions} />
                </Route>
                <Route exact path="/fees-and-settlements">
                    <FeesHubPage
                        financeSummary={data.financeSummary}
                        transactions={data.transactions}
                        organization={data.organization}
                    />
                </Route>
                <Route path="/fees-and-settlements/:view">
                    <FeesDetailPage
                        financeSummary={data.financeSummary}
                        transactions={data.transactions}
                        organization={data.organization}
                    />
                </Route>
                <Route exact path="/loyalty">
                    <LoyaltyHubPage loyaltySummary={data.loyaltySummary} rewardTransactions={data.rewardTransactions} resourceErrors={data.resourceErrors} />
                </Route>
                <Route path="/loyalty/:view">
                    <LoyaltyDetailPage loyaltySummary={data.loyaltySummary} rewardTransactions={data.rewardTransactions} />
                </Route>
                <Route exact path="/menu">
                    <MenuPage token={token} locations={data.locations} />
                </Route>
                <Route exact path="/support">
                    <SupportHubPage
                        supportRequests={data.supportRequests}
                        locations={data.locations}
                        onCreateSupportRequest={data.onCreateSupportRequest}
                        pending={data.supportPending}
                        error={data.supportError}
                    />
                </Route>
                <Route path="/support/:view">
                    <SupportDetailPage
                        supportRequests={data.supportRequests}
                        locations={data.locations}
                        onCreateSupportRequest={data.onCreateSupportRequest}
                        pending={data.supportPending}
                        error={data.supportError}
                    />
                </Route>
                <Redirect to="/overview" />
            </Switch>
        </div>
    );
}

function PartnerPortal() {
    const history = useHistory();
    const [token, setToken] = useState(() => getPartnerToken());
    const [data, setData] = useState(null);
    const [pending, setPending] = useState(Boolean(getPartnerToken()));
    const [loginPending, setLoginPending] = useState(false);
    const [logoutPending, setLogoutPending] = useState(false);
    const [supportPending, setSupportPending] = useState(false);
    const [supportError, setSupportError] = useState('');
    const [error, setError] = useState('');

    const loadPortalData = useCallback(async (activeToken) => {
        setPending(true);
        setError('');

        try {
            const resourceErrors = {};
            const loadOptional = async (key, requestFactory, fallback) => {
                try {
                    return await requestFactory();
                } catch (requestError) {
                    resourceErrors[key] = requestError.message || `Unable to load ${key}.`;
                    return fallback;
                }
            };

            const [profile, organization, locations, bills] = await Promise.all([
                partnerGet('/organizations/my/users/me', activeToken),
                partnerGet('/organizations/my', activeToken),
                loadOptional('locations', () => partnerGet('/organizations/my/locations', activeToken), []),
                partnerGet('/organizations/my/bills', activeToken),
            ]);
            const [transactions, transactionSummary, financeSummary, loyaltySummary, rewardTransactions, supportRequests] = await Promise.all([
                loadOptional('transactions', () => partnerGet(`/organizations/my/transactions?${buildPaginationQuery(1, 50)}`, activeToken), []),
                loadOptional('transactionSummary', () => partnerGet('/organizations/my/transactions/summary', activeToken), {}),
                loadOptional('financeSummary', () => partnerGet('/organizations/my/finance-summary', activeToken), {}),
                loadOptional('loyaltySummary', () => partnerGet('/organizations/my/reward-transactions/summary', activeToken), {}),
                loadOptional('rewardTransactions', () => partnerGet(`/organizations/my/reward-transactions?${buildPaginationQuery(1, 50)}`, activeToken), []),
                loadOptional('supportRequests', () => partnerGet('/organizations/my/support-requests', activeToken), []),
            ]);

            setData({
                profile,
                organization,
                locations,
                bills,
                transactions,
                transactionSummary,
                financeSummary,
                loyaltySummary,
                rewardTransactions,
                supportRequests,
                resourceErrors,
            });
        } catch (requestError) {
            if (requestError.status === 401 || requestError.status === 403) {
                unsetPartnerToken();
                setToken(null);
                setData(null);
                history.replace('/login');
            }
            setError(requestError.message || 'Unable to load the restaurant portal.');
        } finally {
            setPending(false);
        }
    }, [history]);

    useEffect(() => {
        if (!token) {
            setPending(false);
            return;
        }

        loadPortalData(token);
    }, [loadPortalData, token]);

    const handleLogin = useCallback(async (email, password) => {
        setLoginPending(true);
        setError('');
        try {
            const response = await partnerLogin(email, password);
            setPartnerToken(response.token);
            setToken(response.token);
            history.replace('/overview');
        } catch (requestError) {
            setError(requestError.message || 'Unable to sign in.');
        } finally {
            setLoginPending(false);
        }
    }, [history]);

    const handleLogout = useCallback(async () => {
        const activeToken = token;
        setLogoutPending(true);
        unsetPartnerToken();
        setToken(null);
        setData(null);
        history.replace('/login');
        if (activeToken) {
            try {
                await partnerLogout(activeToken);
            } catch (requestError) {
                // Local sign-out already completed; ignore remote session cleanup failures.
            }
        }
        setLogoutPending(false);
    }, [history, token]);

    const handleCreateSupportRequest = useCallback(async (payload) => {
        if (!token) {
            return false;
        }

        setSupportPending(true);
        setSupportError('');
        try {
            const createdRequest = await partnerPost('/organizations/my/support-requests', token, payload);
            setData((currentData) => ({
                ...currentData,
                supportRequests: [createdRequest, ...(currentData ? currentData.supportRequests : [])],
            }));
            return true;
        } catch (requestError) {
            setSupportError(requestError.message || 'Unable to submit the support issue.');
            return false;
        } finally {
            setSupportPending(false);
        }
    }, [token]);

    if (!token) {
        return <PartnerLogin onLogin={handleLogin} pending={loginPending} error={error} />;
    }

    if (pending || !data) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                <div className="text-center" data-testid="partner-loading-state">
                    <Spinner color="primary" />
                    <p className="mt-3 mb-0 text-muted">Loading restaurant portal...</p>
                    {error ? <Alert color="danger" className="mt-3">{error}</Alert> : null}
                </div>
            </div>
        );
    }

    return (
        <PortalLayout
            data={{
                ...data,
                onCreateSupportRequest: handleCreateSupportRequest,
                supportPending,
                supportError,
            }}
            token={token}
            onLogout={handleLogout}
            isLoggingOut={logoutPending}
        />
    );
}

export default function PartnerApp() {
    return (
        <Router basename="/partners">
            <Switch>
                <Route path="/login">
                    <PartnerPortal />
                </Route>
                <Route>
                    <PartnerPortal />
                </Route>
            </Switch>
        </Router>
    );
}
